import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustomFeature extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }


  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

        // 2. ✨ 新增：手风琴互斥逻辑（只在当前要展开时触发）
    if (!this.details.open) {
      // 如果有 data-group 属性，按分组互斥
      if (this.dataset.group) {
        const group = this.dataset.group;
        const allAccordions = document.querySelectorAll(`accordion-custom-feature[data-group="${group}"]`);
        allAccordions.forEach((accordion) => {
          if (accordion !== this) {
            accordion.details.open = false;
            accordion.updateActiveState(); // 更新每个被关闭的 accordion 的样式
          }
        });
      } else {
        // 如果没有分组，默认和同级的其他 accordion 互斥
        const parent = this.parentElement;
        if (parent) {
          const siblings = parent.querySelectorAll('accordion-custom-feature');
          siblings.forEach((sibling) => {
            if (sibling !== this) {
              sibling.details.open = false;
              sibling.updateActiveState();
            }
          });
        }
      }
    }

    // 3. 等浏览器完成默认行为后更新当前 accordion 的样式
    requestAnimationFrame(() => {
      this.updateActiveState();
    });
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
    // ✨ 新增：同步更新样式
    this.updateActiveState();
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }

    /**
   * ✨ new addon: update active state style
   */
  updateActiveState() {
    if (this.details.open) {
      this.classList.add('active');
    } else {
      this.classList.remove('active');
    }
  }
}

if (!customElements.get('accordion-custom-feature')) {
  customElements.define('accordion-custom-feature', AccordionCustomFeature);
}